import { Component, type ErrorInfo, type ReactNode } from 'react';
import { AlertTriangle } from 'lucide-react';
import { Button } from '@/components/ui/button';

interface Props {
  children: ReactNode;
  fallback?: ReactNode;
}

interface State {
  error: Error | null;
}

/**
 * Catches render-time crashes.
 *
 * React unmounts the whole tree when a render throws, so without a boundary a
 * single bad component leaves the user staring at a blank white page with no
 * indication anything went wrong. Must be a class — there is still no hook
 * equivalent of `componentDidCatch`.
 */
export class ErrorBoundary extends Component<Props, State> {
  override state: State = { error: null };

  static getDerivedStateFromError(error: Error): State {
    return { error };
  }

  override componentDidCatch(error: Error, info: ErrorInfo): void {
    // Wire this to a reporting service when one exists.
    console.error('Unhandled render error:', error, info.componentStack);
  }

  override render(): ReactNode {
    const { error } = this.state;
    if (!error) return this.props.children;
    if (this.props.fallback) return this.props.fallback;

    return (
      <div
        role="alert"
        className="flex min-h-dvh flex-col items-center justify-center gap-4 p-6 text-center"
      >
        <div className="flex size-12 items-center justify-center rounded-full bg-destructive/10">
          <AlertTriangle className="size-6 text-destructive" aria-hidden="true" />
        </div>

        <div className="space-y-1">
          <h1 className="text-lg font-semibold">Something went wrong</h1>
          <p className="max-w-md text-sm text-muted-foreground">
            The page failed to render. Reloading usually clears it.
          </p>
        </div>

        {/*
          Shown in production too. This is an internal HR tool used by staff,
          not a public site — hiding the message from the one person who could
          report it just turns every crash into "it broke", which is useless.
          The stack stays in the console.
        */}
        <pre className="max-w-xl overflow-x-auto rounded-md bg-muted p-3 text-left font-mono text-xs">
          {error.message}
        </pre>

        {/* A full reload, not `setState({error: null})` — retrying a render
            against the same broken state just throws again. */}
        <Button onClick={() => window.location.reload()}>Reload the page</Button>
      </div>
    );
  }
}
